import { parsePdfWithCloudmersive } from './cloudmersive-adapter.js';
import { clasificarRegimenContributivo } from '../../core/engine/classificacionContributiva.js';
import { normalizarDiasAnses, pickDiasFromSource } from './anses-dias-normalizer.js';

function toPeriodoDisplay(ano, mes) {
  return String(mes).padStart(2, '0') + '/' + String(ano);
}

function toPeriodoISO(ano, mes) {
  return String(ano) + '-' + String(mes).padStart(2, '0');
}

function mapTipo(tipoRaw) {
  const tipo = (tipoRaw || '').toLowerCase();
  return tipo === 'autonomo' ? 'Autónomo' : 'Dependencia';
}

function cleanNoiseText(value) {
  return String(value || '')
    .replace(/Automa|matizado|Gal|Gaal/gi, '')
    .replace(/[^\x00-\x7F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function detectarRegimenEspecial(raw = {}) {
  const bag = [
    raw?.tipoActividad,
    raw?.tipo,
    raw?.tipoRaw,
    raw?.caja,
    raw?.fuenteNormativa,
    raw?.empresa,
    raw?.razonSocial,
  ].map(v => String(v || '').toLowerCase()).join(' ')
  if (/construccion|ley\s*22\.250|uocra|construc/i.test(bag)) return 'construccion'
  if (/agrario|rural|trabajador agrario|ley\s*21\.463|ley\s*25\.191|renatea|registro nacional de trabajadores agrarios/i.test(bag)) return 'agrario'
  if (/servicio domestico|casas particulares|ley\s*26\.844|empleada domestica|empleo domestico/i.test(bag)) return 'servicio_domestico'
  return null
}

const TOKENS_BASURA_ANSES = [
  'apellido y nombre', 'nro de documento', 'historia laboral',
  'cuit/l vigente', 'resumen historia', 'razón social',
  'detalle de pagos', 'historico de autonomos', 'padrón único',
  'total general', 'subtotal', 'página',
]

function consolidarSinCuitEnParser(rows) {
  const sinCuitPorKey = new Map()
  const conCuit = []

  for (const row of rows) {
    const cuit = String(row.cuit || '').trim()
    const sinCuit = !cuit || cuit === 'SIN_DATO' || cuit === 'NULL' || cuit === 'N/A'
    if (!sinCuit) {
      conCuit.push(row)
      continue
    }
    const key = row.periodoISO + '::' + (row.empresa || '')
    if (!sinCuitPorKey.has(key)) sinCuitPorKey.set(key, [])
    sinCuitPorKey.get(key).push(row)
  }

  for (const grupo of sinCuitPorKey.values()) {
    if (grupo.length === 1) {
      conCuit.push(grupo[0])
      continue
    }
    const base = { ...grupo[0] }
    base.monto = grupo.reduce((s, r) => s + r.monto, 0)
    base.valorFinal = base.monto
    base.remOriginal = base.monto
    const empresas = [...new Set(grupo.map(r => r.empresa).filter(Boolean))]
    if (empresas.length > 1) {
      base.empresa = empresas.join(' + ')
      base.razonSocial = empresas.join(' + ')
    }
    base.requiereRevision = true
    base.observacionSimultaneidad = `Consolidado en parser: ${grupo.length} filas SIN_CUIT en mismo periodo`
    conCuit.push(base)
  }

  return conCuit
}

async function processAnsesPDF(file, options = {}) {
  const arrayBuffer = await file.arrayBuffer();
  const response = await parsePdfWithCloudmersive(arrayBuffer, options);

  if (!response || !Array.isArray(response.remuneraciones)) {
    throw new Error('No se detectaron remuneraciones en el PDF.');
  }

  const remuneracionesLimpias = response.remuneraciones.filter(r => {
    const empresaBag = String(r.empresa || r.razonSocial || '').toLowerCase()
    const tipoBag = String(r.tipo || r.tipoActividad || '').toLowerCase()
    if (TOKENS_BASURA_ANSES.some(t => empresaBag.includes(t) || tipoBag.includes(t))) {
      return false
    }
    if (!r.ano && !r.mes && !r.monto && !r.empresa && !r.razonSocial) {
      return false
    }
    return true
  })

  if (remuneracionesLimpias.length === 0) {
    throw new Error('No se detectaron remuneraciones válidas en el PDF luego del filtro anti-basura.');
  }

  let lastAno = 0;
  let lastMes = 0;
  const titularCuil = String(response?.cuil || '').replace(/\D/g, '');
  const remuneracionesMapeadasBase = remuneracionesLimpias.map(r => {
    const rawAno = Number(r.ano) || 0;
    const rawMes = Number(r.mes) || 0;
    let ano = rawAno;
    let mes = rawMes;
    let fechaReconstruida = false;
    if (!(mes >= 1 && mes <= 12) && lastMes >= 1 && lastMes <= 12) {
      mes = lastMes === 12 ? 1 : lastMes + 1;
      fechaReconstruida = true;
    }
    if (!(ano >= 1900 && ano <= 2100) && lastAno >= 1900 && lastAno <= 2100) {
      ano = lastMes === 12 ? lastAno + 1 : lastAno;
      fechaReconstruida = true;
    }
    if (mes >= 1 && mes <= 12) lastMes = mes;
    if (ano >= 1900 && ano <= 2100) lastAno = ano;

    const monto = typeof r.monto === 'number' && !isNaN(r.monto) ? r.monto : 0;
    const ocrColumnShift = rawAno > 0 && (rawAno === 1994 || rawAno === 1995) && (
      (monto >= 1994 && monto <= 1996) ||
      (monto >= 24240 && monto <= 24242)
    );
    const tipo = mapTipo(r.tipo);
    const tipoRaw = String(r.tipo || 'dependencia').toLowerCase();
    const regimenContributivo = clasificarRegimenContributivo(r, titularCuil);
    const regimenEspecial = detectarRegimenEspecial(r);
    const empresaClean = cleanNoiseText(r.razonSocial || r.empresa || '');
    const empresa = empresaClean || null;
    const cuit = r.cuit || null;
    const requiereRevision =
      !!r.requiereRevision ||
      empresa === null ||
      cuit === null ||
      (tipo === 'Dependencia' && monto === 0) ||
      fechaReconstruida ||
      ocrColumnShift;
    const confidence = Number(r.confidence ?? r.score ?? 1);
    const escalaSospechosa = monto > 0 && monto < 2500 && (monto * 1000) > 50000;
    const requiresValidation = Boolean(
      r.requiresValidation ||
      (Number.isFinite(confidence) && confidence < 0.8) ||
      fechaReconstruida ||
      escalaSospechosa ||
      ocrColumnShift
    );

    return {
      // Esquema estricto pedido para inyección en tabla
      periodo: toPeriodoDisplay(ano || 0, mes || 0),
      empresa,
      cuit,
      monto,
      tipo,
      regimenContributivo,
      regimenEspecial,
      actualizado: false,
      requiereRevision: requiereRevision || requiresValidation || !!regimenEspecial,
      requiresValidation,
      confidence: Number.isFinite(confidence) ? confidence : null,
      // Compatibilidad interna existente
      periodoISO: toPeriodoISO(ano || 0, mes || 0),
      ano,
      mes,
      valorFinal: monto,
      remOriginal: monto,
      razonSocial: empresa,
      observacionFecha: fechaReconstruida ? 'Fecha reconstruida por secuencia' : null,
      observacionMonto: escalaSospechosa
        ? 'Monto sospechoso: verificar escala (posible error de separador de miles — ej. $' + monto + ' o $' + Math.round(monto * 1000) + '?)'
        : ocrColumnShift
          ? 'Posible columna de año leída como monto (1994/1995 detectado como salario)'
          : null,
      tipoRaw: r.tipo || 'dependencia',
      diasOrigenRaw: pickDiasFromSource(r),
      moneda: r.moneda || 'peso',
      monedaNormalizada: false,
      monedaOriginal: null,
    };
  });

  const remuneracionesConsolidadas = consolidarSinCuitEnParser(remuneracionesMapeadasBase)
  const remuneracionesMapeadas = normalizarDiasAnses(remuneracionesConsolidadas)

  return {
    remuneraciones: remuneracionesMapeadas,
    ultimoPeriodoDetectado: response.ultimoPeriodoDetectado || null,
    historico_autonomos: response.historico_autonomos || [],
    historico_desempleo: response.historico_desempleo || [],
    resumenHistoriaLaboral: response.resumenHistoriaLaboral || null,
    resumenHistoriaLaboralDetalle: response.resumenHistoriaLaboralDetalle || [],
    historiaLaboralHasta0694: response.historiaLaboralHasta0694 || [],
    totalesPorCuit: response.totalesPorCuit || response.totales_por_cuit || null,
    cuil: response.cuil || 'No disponible',
    etlSummary: response.etlSummary || null,
    escenariosEstrategia: response.escenariosEstrategia || null,
    etlSummaryConservador: response.etlSummaryConservador || null,
    etlSummaryLitigio: response.etlSummaryLitigio || null,
    markdownTable: response.markdownTable || null,
    markdownTableConsolidada: response.markdownTableConsolidada || null,
    debug: response.debug || null,
    alertas: {
      saltosCronologicos: response.alertas?.saltosCronologicos || [],
      totalSaltos: response.alertas?.saltosCronologicos?.length || 0,
      filasARevisar: remuneracionesMapeadas.filter(r => r.requiereRevision || r.requiereRevisionDias).length,
      filasConDiasNormalizados: remuneracionesMapeadas.filter(r => Number.isInteger(r.diasComputables)).length,
      filasMonedaNormalizada: response.remuneraciones.filter(r => r.monedaNormalizada).length,
      estadoDatos: response.alertas?.estadoDatos || null,
    },
    metadata: {
      totalRemuneraciones: remuneracionesLimpias.length,
      filasFiltradas: response.remuneraciones.length - remuneracionesLimpias.length,
    },
  };
}

export default processAnsesPDF;
